import React from 'react';
import styled from 'styled-components';
import { FiUpload, FiTrash2, FiPlus } from 'react-icons/fi';

const FazyraTouchForm = ({ fazyraTouch, onChange, onImageChange }) => {
  
  // Check if all required fields are filled
  const isFormComplete = () => {
    // Check if images are filled (either URL or preview exists)
    const hasImage1 = (fazyraTouch.image1 && fazyraTouch.image1.trim() !== '') || 
                      (fazyraTouch.image1Preview && fazyraTouch.image1Preview.trim() !== '');
    const hasImage2 = (fazyraTouch.image2 && fazyraTouch.image2.trim() !== '') || 
                      (fazyraTouch.image2Preview && fazyraTouch.image2Preview.trim() !== '');
    const hasImage3 = (fazyraTouch.image3 && fazyraTouch.image3.trim() !== '') || 
                      (fazyraTouch.image3Preview && fazyraTouch.image3Preview.trim() !== '');
    
    // Check if descriptions are filled
    const hasMainDescription = fazyraTouch.mainDescription && fazyraTouch.mainDescription.trim() !== '';
    const hasClosingDescription = fazyraTouch.closingDescription && fazyraTouch.closingDescription.trim() !== '';
    
    // Check if at least 3 features are filled with both label and description
    const filledFeatures = (fazyraTouch.features || []).filter(f => 
      f.label && f.label.trim() !== '' && 
      f.description && f.description.trim() !== ''
    );
    const hasEnoughFeatures = filledFeatures.length >= 3;
    
    return hasImage1 && hasImage2 && hasImage3 && 
           hasMainDescription && hasClosingDescription && 
           hasEnoughFeatures;
  };

  // Handle checkbox toggle with validation
  const handleEnabledToggle = (checked) => {
    if (checked && !isFormComplete()) {
      // Build detailed error message
      const hasImage1 = (fazyraTouch.image1 && fazyraTouch.image1.trim() !== '') || 
                        (fazyraTouch.image1Preview && fazyraTouch.image1Preview.trim() !== '');
      const hasImage2 = (fazyraTouch.image2 && fazyraTouch.image2.trim() !== '') || 
                        (fazyraTouch.image2Preview && fazyraTouch.image2Preview.trim() !== '');
      const hasImage3 = (fazyraTouch.image3 && fazyraTouch.image3.trim() !== '') || 
                        (fazyraTouch.image3Preview && fazyraTouch.image3Preview.trim() !== '');
      const hasMainDescription = fazyraTouch.mainDescription && fazyraTouch.mainDescription.trim() !== '';
      const hasClosingDescription = fazyraTouch.closingDescription && fazyraTouch.closingDescription.trim() !== '';
      const filledFeatures = (fazyraTouch.features || []).filter(f => 
        f.label && f.label.trim() !== '' && 
        f.description && f.description.trim() !== ''
      );
      
      let errorMsg = 'Please complete all required fields before enabling Fazyra Touch section:\n\n';
      if (!hasImage1) errorMsg += '✗ Image 1 is missing\n';
      if (!hasImage2) errorMsg += '✗ Image 2 is missing\n';
      if (!hasImage3) errorMsg += '✗ Image 3 is missing\n';
      if (!hasMainDescription) errorMsg += '✗ Main description is empty\n';
      if (filledFeatures.length < 3) errorMsg += `✗ Need at least 3 complete features (currently have ${filledFeatures.length})\n`;
      if (!hasClosingDescription) errorMsg += '✗ Closing description is empty\n';
      
      alert(errorMsg);
      return;
    }
    onChange('fazyraTouch.enabled', checked);
  };

  // Handle feature updates
  const handleFeatureChange = (index, field, value) => {
    const updatedFeatures = [...(fazyraTouch.features || [])];
    updatedFeatures[index] = {
      ...updatedFeatures[index],
      [field]: value
    };
    onChange('fazyraTouch.features', updatedFeatures);
  };

  // Add new feature
  const addFeature = () => {
    const updatedFeatures = [...(fazyraTouch.features || []), { label: '', description: '' }];
    onChange('fazyraTouch.features', updatedFeatures);
  };

  // Remove feature
  const removeFeature = (index) => {
    const updatedFeatures = (fazyraTouch.features || []).filter((_, i) => i !== index);
    onChange('fazyraTouch.features', updatedFeatures);
  };

  return (
    <FazyraTouchContainer>
      <SectionHeader>
        <SectionTitle>Fazyra Touch Section (Optional)</SectionTitle>
        <EnableCheckbox>
          <input
            type="checkbox"
            checked={fazyraTouch.enabled || false}
            onChange={(e) => handleEnabledToggle(e.target.checked)}
            id="fazyraTouchEnabled"
          />
          <label htmlFor="fazyraTouchEnabled">
            Enable Fazyra Touch Section
            {!isFormComplete() && fazyraTouch.enabled && (
              <WarningText> (Complete form to enable)</WarningText>
            )}
          </label>
        </EnableCheckbox>
      </SectionHeader>

      <FormDescription>
        The Fazyra Touch section displays 3 images, a title, description, features list, and closing text.
        {!isFormComplete() && (
          <RequiredNote>All fields must be completed to enable this section.</RequiredNote>
        )}
      </FormDescription>

      {/* Images Section */}
      <SubSection>
        <SubSectionTitle>Images (3 Required) *</SubSectionTitle>
        <ImagesGrid>
          {[1, 2, 3].map((num) => (
            <ImageUploadCard key={num}>
              <ImageLabel>Image {num} *</ImageLabel>
              {fazyraTouch[`image${num}Preview`] && (
                <ImagePreview>
                  <img src={fazyraTouch[`image${num}Preview`]} alt={`Preview ${num}`} />
                </ImagePreview>
              )}
              <ImageInput
                type="text"
                placeholder="Image URL (https://...)"
                value={fazyraTouch[`image${num}`] || ''}
                onChange={(e) => onChange(`fazyraTouch.image${num}`, e.target.value)}
              />
              <FileInputLabel htmlFor={`fazyraImage${num}`}>
                <FiUpload size={14} /> Upload Image {num}
              </FileInputLabel>
              <FileInput
                id={`fazyraImage${num}`}
                type="file"
                accept="image/*"
                onChange={(e) => onImageChange(`image${num}`, e.target.files[0])}
              />
              <ImageInput
                type="text"
                placeholder="Alt text (optional)"
                value={fazyraTouch[`image${num}Alt`] || ''}
                onChange={(e) => onChange(`fazyraTouch.image${num}Alt`, e.target.value)}
                style={{ marginTop: '8px', fontSize: '13px' }}
              />
            </ImageUploadCard>
          ))}
        </ImagesGrid>
      </SubSection>

      {/* Content Section */}
      <SubSection>
        <SubSectionTitle>Content</SubSectionTitle>
        
        <FormGroup>
          <Label>Title</Label>
          <Input
            type="text"
            placeholder="The Fazyra Touch"
            value={fazyraTouch.title || ''}
            onChange={(e) => onChange('fazyraTouch.title', e.target.value)}
          />
        </FormGroup>

        <FormGroup>
          <Label>Subtitle</Label>
          <Input
            type="text"
            placeholder="Where Every Detail Tells Your Story"
            value={fazyraTouch.subtitle || ''}
            onChange={(e) => onChange('fazyraTouch.subtitle', e.target.value)}
          />
        </FormGroup>

        <FormGroup>
          <Label>Main Description *</Label>
          <Textarea
            placeholder="Introduce the Fazyra Touch concept..."
            value={fazyraTouch.mainDescription || ''}
            onChange={(e) => onChange('fazyraTouch.mainDescription', e.target.value)}
            rows={4}
          />
        </FormGroup>
      </SubSection>

      {/* Features Section */}
      <SubSection>
        <SubSectionTitle>
          Features (Minimum 3 Required) *
          <AddFeatureBtn onClick={addFeature}>
            <FiPlus size={14} /> Add Feature
          </AddFeatureBtn>
        </SubSectionTitle>

        {(fazyraTouch.features || []).map((feature, index) => (
          <FeatureCard key={index}>
            <FeatureHeader>
              <FeatureNumber>Feature {index + 1}</FeatureNumber>
              {(fazyraTouch.features || []).length > 1 && (
                <RemoveFeatureBtn onClick={() => removeFeature(index)}>
                  <FiTrash2 size={14} /> Remove
                </RemoveFeatureBtn>
              )}
            </FeatureHeader>
            <FormGroup>
              <Label>Label/Title *</Label>
              <Input
                type="text"
                placeholder="e.g., Personalized Design"
                value={feature.label || ''}
                onChange={(e) => handleFeatureChange(index, 'label', e.target.value)}
              />
            </FormGroup>
            <FormGroup>
              <Label>Description *</Label>
              <Textarea
                placeholder="e.g., Custom themes tailored to your vision and preferences"
                value={feature.description || ''}
                onChange={(e) => handleFeatureChange(index, 'description', e.target.value)}
                rows={2}
              />
            </FormGroup>
          </FeatureCard>
        ))}

        {(!fazyraTouch.features || fazyraTouch.features.length === 0) && (
          <EmptyState onClick={addFeature}>
            <FiPlus size={32} color="#ccc" />
            <p>Click to add your first feature</p>
          </EmptyState>
        )}
      </SubSection>

      {/* Closing Description */}
      <SubSection>
        <SubSectionTitle>Closing Description *</SubSectionTitle>
        <FormGroup>
          <Textarea
            placeholder="Conclude with a compelling message..."
            value={fazyraTouch.closingDescription || ''}
            onChange={(e) => onChange('fazyraTouch.closingDescription', e.target.value)}
            rows={3}
          />
        </FormGroup>
      </SubSection>

      {/* Completion Status */}
      <CompletionStatus isComplete={isFormComplete()}>
        {isFormComplete() ? (
          <>✓ Form is complete and ready to enable</>
        ) : (
          <>⚠ Complete all required fields to enable this section</>
        )}
      </CompletionStatus>
    </FazyraTouchContainer>
  );
};

// Styled Components
const FazyraTouchContainer = styled.div`
  background: #f8f9fa;
  border-radius: 12px;
  padding: 24px;
  margin-top: 24px;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0;
`;

const EnableCheckbox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
  }

  label {
    font-size: 14px;
    font-weight: 500;
    color: #555;
    cursor: pointer;
  }
`;

const WarningText = styled.span`
  color: #ff6b6b;
  font-size: 12px;
  font-style: italic;
`;

const FormDescription = styled.p`
  font-size: 14px;
  color: #666;
  margin-bottom: 20px;
  line-height: 1.5;
`;

const RequiredNote = styled.span`
  display: block;
  color: #ff6b6b;
  font-weight: 500;
  margin-top: 8px;
`;

const SubSection = styled.div`
  background: white;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 16px;
`;

const SubSectionTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ImagesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ImageUploadCard = styled.div`
  border: 2px dashed #ddd;
  border-radius: 8px;
  padding: 16px;
  text-align: center;
`;

const ImageLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 12px;
`;

const ImagePreview = styled.div`
  width: 100%;
  height: 150px;
  margin-bottom: 12px;
  border-radius: 6px;
  overflow: hidden;
  background: #f5f5f5;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ImageInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 13px;
  margin-bottom: 8px;

  &:focus {
    outline: none;
    border-color: #4CAF50;
  }
`;

const FileInputLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: #4CAF50;
  color: white;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #45a049;
  }
`;

const FileInput = styled.input`
  display: none;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #4CAF50;
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #4CAF50;
  }
`;

const AddFeatureBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #4CAF50;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #45a049;
  }
`;

const FeatureCard = styled.div`
  background: #f8f9fa;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 16px;
  margin-bottom: 12px;
`;

const FeatureHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const FeatureNumber = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #555;
`;

const RemoveFeatureBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: #ff6b6b;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #ff5252;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #999;
  cursor: pointer;
  border: 2px dashed #ddd;
  border-radius: 8px;
  transition: all 0.2s;

  &:hover {
    border-color: #4CAF50;
    color: #4CAF50;
  }

  p {
    margin-top: 12px;
    font-size: 14px;
  }
`;

const CompletionStatus = styled.div`
  padding: 12px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  text-align: center;
  background: ${props => props.isComplete ? '#d4edda' : '#fff3cd'};
  color: ${props => props.isComplete ? '#155724' : '#856404'};
  border: 1px solid ${props => props.isComplete ? '#c3e6cb' : '#ffeaa7'};
`;

export default FazyraTouchForm;
